// 2. Updated Processing Page Component using your existing API
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import ESignService from '../../services/ESignService';
import {
  Container,
  Paper,
  Typography,
  LinearProgress,
  Box,
  CircularProgress,
  Button,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import PendingIcon from '@material-ui/icons/Schedule';
import SignedIcon from '@material-ui/icons/Done';
import cargoesLogo from '../../images/Cargoes.png';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5'
  },
  appBar: {
    backgroundColor: '#1976d2'
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  paper: {
    padding: theme.spacing(4),
    textAlign: 'center',
    maxWidth: 700,
    margin: '0 auto'
  },
  progressContainer: {
    margin: theme.spacing(3, 0),
    textAlign: 'left'
  },
  statusIcon: {
    fontSize: 64,
    marginBottom: theme.spacing(2)
  },
  successIcon: {
    color: theme.palette.success.main
  },
  errorIcon: {
    color: theme.palette.error.main
  },
  processingIcon: {
    color: theme.palette.primary.main
  },
  button: {
    marginTop: theme.spacing(2)
  },
  spinner: {
    margin: theme.spacing(2)
  },
  recipientsList: {
    textAlign: 'left',
    marginTop: theme.spacing(2)
  },
  documentInfo: {
    marginBottom: theme.spacing(3),
    textAlign: 'left'
  }
}));

const ProcessingPage = () => {
  const classes = useStyles();
  const { documentId } = useParams();
  const history = useHistory();
  const [documentStatus, setDocumentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch document status manually
  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const response = await ESignService.getDocument(documentId);
      setDocumentStatus(response.data);
      setIsLoading(false);
      // Check if process is complete (either all signed or document rejected)
      const progress = calculateProgressFromResponse(response.data);
      const isProcessComplete = response.data.status === 'completed' || response.data.status === 'rejected' || progress === 100;
      
      if (isProcessComplete) {
        setTimeout(() => {
          history.push('/e-sign/complete');
        }, 3000);
      } else if (response.data.status === 'failed') {
        setError('Document signing failed. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check document status');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus(); // Initial fetch
    
    // Set up automatic refresh every 5 seconds
    const interval = setInterval(() => {
      fetchStatus();
    }, 5000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [documentId, history]);

  const handleRetry = () => {
    history.goBack();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'signed':
        return 'primary';
      case 'signing':
        return 'secondary';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'signed':
        return <SignedIcon />;
      case 'signing':
        return <PendingIcon />;
      case 'pending':
        return <PendingIcon />;
      default:
        return <PendingIcon />;
    }
  };

  // Helper function to calculate progress from response data
  const calculateProgressFromResponse = (responseData) => {
    if (!responseData || !responseData.recipients) return 0;
    
    const totalRecipients = responseData.recipients.length;
    const signedRecipients = responseData.recipients.filter(r => r.status === 'signed').length;
    const rejectedRecipients = responseData.recipients.filter(r => r.status === 'rejected').length;
    const signingRecipients = responseData.recipients.filter(r => r.status === 'signing').length;
    
    // Both signed and rejected recipients have completed their action
    const completedRecipients = signedRecipients + rejectedRecipients;
    
    // Give partial credit for recipients currently signing
    return Math.round(((completedRecipients + (signingRecipients * 0.5)) / totalRecipients) * 100);
  };

  const calculateProgress = () => {
    return calculateProgressFromResponse(documentStatus);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <CircularProgress size={64} className={classes.spinner} />
          <Typography variant="h4" gutterBottom>
            Loading Document Status...
          </Typography>
        </>
      );
    }

    if (error) {
      return (
        <>
          <ErrorIcon className={`${classes.statusIcon} ${classes.errorIcon}`} />
          <Typography variant="h4" gutterBottom>
            Signing Failed
          </Typography>
          <Typography variant="body1" paragraph color="error">
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchStatus}
            className={classes.button}
          >
            Refresh Status
          </Button>
        </>
      );
    }

    if (!documentStatus) {
      return (
        <>
          <ErrorIcon className={`${classes.statusIcon} ${classes.errorIcon}`} />
          <Typography variant="h4" gutterBottom>
            Document Not Found
          </Typography>
          <Typography variant="body1" paragraph>
            Unable to load document status.
          </Typography>
        </>
      );
    }

    if (documentStatus.status === 'completed') {
      return (
        <>
          <CheckCircleIcon className={`${classes.statusIcon} ${classes.successIcon}`} />
          <Typography variant="h4" gutterBottom>
            Document Signed Successfully!
          </Typography>
          <Typography variant="body1" paragraph>
            All signatures have been applied to "{documentStatus.title}".
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Redirecting to completion page...
          </Typography>
        </>
      );
    }

    // Processing state
    const progress = calculateProgress();
    const isProcessing = Array.isArray(documentStatus.recipients) && documentStatus.recipients.some(r => r.status === 'signing');
    const hasRejections = Array.isArray(documentStatus.recipients) && documentStatus.recipients.some(r => r.status === 'rejected');
    const allCompleted = progress === 100;
    
    // Determine the appropriate title based on document state
    let pageTitle;
    if (allCompleted && hasRejections) {
      pageTitle = 'Document Process Completed';
    } else if (hasRejections) {
      pageTitle = 'Document Rejected';
    } else if (isProcessing) {
      pageTitle = 'Processing Signatures...';
    } else {
      pageTitle = 'Waiting for Signatures';
    }
    
    return (
      <>
        {isLoading && <CircularProgress size={64} className={classes.spinner} />}
        <Typography variant="h4" gutterBottom>
          {pageTitle}
        </Typography>
        
        <Box className={classes.documentInfo}>
          <Typography variant="h6" gutterBottom>
            Document: {documentStatus.title}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Sent by: {documentStatus.sentBy}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Sent at: {new Date(documentStatus.sentAt).toLocaleString()}
          </Typography>
        </Box>
        
        <Box className={classes.progressContainer}>
          <Typography variant="body1" gutterBottom>
            Signature Progress ({progress}%)
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            style={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        
        <Box className={classes.recipientsList}>
          <Typography variant="h6" gutterBottom>
            Recipients Status:
          </Typography>
          <List>
            {Array.isArray(documentStatus.recipients) && documentStatus.recipients.map((recipient) => (
              <ListItem key={recipient.id}>
                <ListItemIcon>
                  {getStatusIcon(recipient.status)}
                </ListItemIcon>
                <ListItemText 
                  primary={recipient.name || recipient.email}
                  secondary={
                    recipient.status === 'signed' 
                      ? `Signed at: ${new Date(recipient.signedAt).toLocaleString()}`
                      : recipient.status === 'rejected'
                      ? `Rejected: ${recipient.rejectionComment || 'No reason provided'}`
                      : `Status: ${recipient.status}`
                  }
                />
                <Chip 
                  label={recipient.status.toUpperCase()} 
                  color={getStatusColor(recipient.status)}
                  size="small"
                />
              </ListItem>
            ))}
          </List>
        </Box>
        
        {isProcessing && (
          <Typography variant="body2" color="textSecondary">
            Signatures are being processed. Please wait...
          </Typography>
        )}
        
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
          Auto-refreshing every 5 seconds...
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          onClick={fetchStatus}
          className={classes.button}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh Now'}
        </Button>
      </>
    );
  };

  return (
    <div className={classes.root}>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar>
          <img src={cargoesLogo} alt="Cargoes Logo" style={{ height: 30, marginRight: 16 }} />
          <Typography variant="h6">e-Sign</Typography>
        </Toolbar>
      </AppBar>
      
      <Container className={classes.container}>
        <Paper className={classes.paper}>
          {renderContent()}
        </Paper>
      </Container>
    </div>
  );
};

export default ProcessingPage;